import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, IconButton } from '@mui/material';
import { Star, StarBorder } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useListWorkflows } from '../../../clients/graphql/pages/workflow-queries';

interface Workflow {
  id: string;
  name: string;
  activeExecutions: number;
}

const PINNED_WORKFLOWS_KEY = 'pinnedWorkflows';

export const WorkflowsPage: React.FC = () => {
  const { loading, error, data } = useListWorkflows();
  const [pinnedWorkflows, setPinnedWorkflows] = useState<Set<string>>(new Set());
  const [isInitialized, setIsInitialized] = useState(false);

  // Load pinned workflows from localStorage on component mount
  useEffect(() => {
    const stored = localStorage.getItem(PINNED_WORKFLOWS_KEY);
    if (stored) {
      try {
        const pinnedIds = JSON.parse(stored);
        setPinnedWorkflows(new Set(pinnedIds));
      } catch (e) {
        console.error('Failed to parse pinned workflows from localStorage:', e);
      }
    }
    setIsInitialized(true);
  }, []);

  // Save pinned workflows to localStorage whenever they change (but not on initial load)
  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem(PINNED_WORKFLOWS_KEY, JSON.stringify(Array.from(pinnedWorkflows)));
    }
  }, [pinnedWorkflows, isInitialized]);

  const togglePinWorkflow = (workflowId: string) => {
    setPinnedWorkflows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(workflowId)) {
        newSet.delete(workflowId);
      } else {
        newSet.add(workflowId);
      }
      return newSet;
    });
  };

  // Sort workflows: pinned first, then alphabetically by name
  const sortedWorkflows = useMemo(() => {
    if (!data?.listWorkflows) return [];
    
    const workflows = [...data.listWorkflows];
    
    return workflows.sort((a: Workflow, b: Workflow) => {
      const aIsPinned = pinnedWorkflows.has(a.id);
      const bIsPinned = pinnedWorkflows.has(b.id);
      
      // If one is pinned and the other isn't, pinned comes first
      if (aIsPinned && !bIsPinned) return -1;
      if (!aIsPinned && bIsPinned) return 1;
      
      // If both are pinned or both are not pinned, sort alphabetically by name
      return a.name.localeCompare(b.name);
    });
  }, [data?.listWorkflows, pinnedWorkflows]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error loading workflows: {error.message}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Workflows
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="48px"></TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Active Executions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedWorkflows.map((workflow: Workflow) => (
              <TableRow
                key={workflow.id}
                sx={{ '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
              >
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => togglePinWorkflow(workflow.id)}
                    sx={{ color: pinnedWorkflows.has(workflow.id) ? 'primary.main' : 'text.secondary' }}
                  >
                    {pinnedWorkflows.has(workflow.id) ? <Star /> : <StarBorder />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <Link 
                    to={`/developer/workflows/${workflow.id}`}
                    style={{ 
                      textDecoration: 'none', 
                      color: 'inherit',
                      display: 'block',
                      width: '100%',
                      cursor: 'pointer'
                    }}
                  >
                    {workflow.name}
                  </Link>
                </TableCell>
                <TableCell>
                  <Link 
                    to={`/developer/workflows/${workflow.id}`}
                    style={{ 
                      textDecoration: 'none', 
                      color: 'inherit',
                      display: 'block',
                      width: '100%',
                      cursor: 'pointer'
                    }}
                  >
                    {workflow.activeExecutions}
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}; 